import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChartDonut, Warning, FileCsv, Printer } from '@phosphor-icons/react';
import Typhography from '../../shared/Typhography/Typhography';
import LeopartLoader from '../../shared/LeopartLoader/LeopartLoader';
import CustomButton from '../../shared/CustomButton/CustomButton';
import styles from './TrainingStatisticsDashboard.module.scss';
import { useTrainingStatistics } from './hooks/useTrainingStatistics';
import StatsFiltersBar from './components/StatsFiltersBar';
import StatsKpiRow from './components/StatsKpiRow';
import PhaseDistributionChart from './components/PhaseDistributionChart';
import MethodDistributionChart from './components/MethodDistributionChart';
import ObjectivesRankChart from './components/ObjectivesRankChart';
import InsightsPanel from './components/InsightsPanel';
import { ObjectiveDrillDownModal } from './components/ObjectiveDrillDownModal';
import { TagBiasWidget } from './components/TagBiasWidget';
import { ComparePeriodSummary } from './components/ComparePeriodSummary';
import type { PeriodPreset } from './hooks/useTrainingStatistics';
import { exportStatsAsCsv } from './exportStats';
import { exportStatsAsPdf } from './exportPdf';

/**
 * Pagina Dashboard Statistiche Allenamento.
 * Route: /dashboard/teams/:teamId/training-stats
 * Spec: LEOPART_ProgrammazioneCampo_Spec_v1.1.1.md Sez 21 righe 2749-3062.
 *
 * Layout 7 sezioni:
 * 1. Filtri globali
 * 2. KPI Overview
 * 3. Distribuzione Fasi Seduta (Pie)
 * 4. Distribuzione Metodi Didattici (Bar orizzontale)
 * 5. Top 10 Obiettivi Tecnici
 * 6. Top 10 Obiettivi Tattici
 * 7. Insights / Alert bilanciamento
 */
const TrainingStatisticsDashboard: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();

  const {
    stats,
    previousStats,
    isLoading,
    error,
    preset,
    currentRange,
    changePreset,
    changeCustomRange,
    recalculate,
    isRecalculating,
    compareEnabled,
    setCompareEnabled,
  } = useTrainingStatistics({ teamId });

  const [athleteFilter, setAthleteFilter] = useState('');

  const handlePresetChange = useCallback(
    (p: PeriodPreset) => changePreset(p),
    [changePreset],
  );

  const handleRecalculate = useCallback(() => {
    recalculate().catch(() => undefined);
  }, [recalculate]);

  const aggregates = stats?.aggregates;
  const insights = stats?.insights ?? [];

  const [drillDown, setDrillDown] = useState<{
    objectiveId: string;
    kind: 'technical' | 'tactical';
  } | null>(null);

  const handleExportCsv = () => {
    if (!aggregates) return;
    exportStatsAsCsv(aggregates, teamId ?? 'team');
  };

  const handleExportPdf = () => {
    if (!aggregates) return;
    const label = `${new Date(currentRange.start).toLocaleDateString('it-IT')} – ${new Date(currentRange.end).toLocaleDateString('it-IT')}`;
    exportStatsAsPdf(aggregates, teamId ?? 'Team', label);
  };

  const handlePrint = () => window.print();

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.scrollArea}>
        {/* Title row */}
        <div className={styles.titleRow}>
          <div className={styles.titleBlock}>
            <p className={styles.pageTitle}>Statistiche Allenamento</p>
            <p className={styles.pageSubtitle}>
              Analisi del tempo dedicato a fasi, metodi e obiettivi nel periodo selezionato
            </p>
          </div>
          {aggregates && aggregates.totalSessions > 0 && (
            <div className={styles.titleActions}>
              <CustomButton
                color="outline"
                size="sm"
                onClick={handleExportCsv}
              >
                <FileCsv size={13} /> CSV
              </CustomButton>
              <CustomButton
                color="outline"
                size="sm"
                onClick={handleExportPdf}
              >
                <Printer size={13} /> PDF
              </CustomButton>
            </div>
          )}
        </div>

        {/* Filtri */}
        <StatsFiltersBar
          preset={preset}
          onPresetChange={handlePresetChange}
          customStart={currentRange.start}
          customEnd={currentRange.end}
          onCustomRange={changeCustomRange}
          onRecalculate={handleRecalculate}
          isRecalculating={isRecalculating}
          compareEnabled={compareEnabled}
          onCompareToggle={setCompareEnabled}
          athleteFilter={athleteFilter}
          onAthleteFilterChange={setAthleteFilter}
          availableAthletes={[]}
        />

        {/* Loading */}
        {isLoading && (
          <div className={styles.loadingState}>
            <LeopartLoader />
            <Typhography size="regular-14" className={styles.loadingText}>
              Calcolo statistiche in corso...
            </Typhography>
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <div className={styles.errorState}>
            <Warning size={20} weight="fill" style={{ color: '#dc2626' }} />
            <Typhography size="regular-14" className={styles.errorText}>
              Impossibile caricare le statistiche. Riprova.
            </Typhography>
          </div>
        )}

        {/* Empty (no team) */}
        {!isLoading && !error && !teamId && (
          <div className={styles.emptyState}>
            <ChartDonut size={40} weight="thin" style={{ color: 'var(--c-text-3)' }} />
            <Typhography size="regular-14" className={styles.emptyText}>
              Seleziona un team per visualizzare le statistiche.
            </Typhography>
          </div>
        )}

        {/* Content */}
        {!isLoading && !error && stats && aggregates && (
          <>
            {/* Sezione 2: KPI */}
            <div className={styles.section}>
              <Typhography size="bold-16" className={styles.sectionTitle}>
                Panoramica periodo
              </Typhography>
              <div className={styles.sectionDivider} />
              <StatsKpiRow aggregates={aggregates} />
              {compareEnabled && previousStats?.aggregates && (
                <ComparePeriodSummary
                  current={aggregates}
                  previous={previousStats.aggregates}
                />
              )}
            </div>

            {/* Sezione 3: Distribuzione fasi */}
            <div className={styles.section}>
              <Typhography size="bold-16" className={styles.sectionTitle}>
                Distribuzione per fase seduta
              </Typhography>
              <div className={styles.sectionDivider} />
              <PhaseDistributionChart data={aggregates.bySessionPhase ?? []} />
            </div>

            {/* Sezione 4: Metodi didattici */}
            <div className={styles.section}>
              <Typhography size="bold-16" className={styles.sectionTitle}>
                Distribuzione per metodo didattico
              </Typhography>
              <div className={styles.sectionDivider} />
              <MethodDistributionChart data={aggregates.byDidacticMethod ?? []} />
            </div>

            {/* Sezione 5: Obiettivi tecnici */}
            <div className={styles.section}>
              <ObjectivesRankChart
                data={aggregates.byTechnicalObjective ?? []}
                title="Top obiettivi tecnici allenati"
                color="#063444"
                onObjectiveClick={(id) =>
                  setDrillDown({ objectiveId: id, kind: 'technical' })
                }
              />
            </div>

            {/* Sezione 6: Obiettivi tattici */}
            <div className={styles.section}>
              <ObjectivesRankChart
                data={aggregates.byTacticalObjective ?? []}
                title="Top obiettivi tattici allenati"
                color="#1a8fb0"
                onObjectiveClick={(id) =>
                  setDrillDown({ objectiveId: id, kind: 'tactical' })
                }
              />
            </div>

            {/* Sezione 7: Insights */}
            <div className={styles.section}>
              <Typhography size="bold-16" className={styles.sectionTitle}>
                Analisi bilanciamento
              </Typhography>
              <div className={styles.sectionDivider} />
              <InsightsPanel insights={insights} />
            </div>

            {/* Sezione 8: Tag Bias GPS */}
            {teamId && (
              <div className={styles.section}>
                <Typhography size="bold-16" className={styles.sectionTitle}>
                  Calibrazione IPS — bias tag GPS
                </Typhography>
                <div className={styles.sectionDivider} />
                <TagBiasWidget teamId={teamId} />
              </div>
            )}
          </>
        )}

        {/* Stats with no aggregates yet */}
        {!isLoading && !error && stats && !aggregates?.totalSessions && (
          <div className={styles.emptyState}>
            <ChartDonut size={40} weight="thin" style={{ color: 'var(--c-text-3)' }} />
            <Typhography size="regular-14" className={styles.emptyText}>
              Nessuna sessione classificata nel periodo selezionato.
            </Typhography>
          </div>
        )}
      </div>

      {drillDown && aggregates && (
        <ObjectiveDrillDownModal
          objectiveId={drillDown.objectiveId}
          kind={drillDown.kind}
          aggregates={aggregates}
          teamId={teamId}
          periodStart={currentRange.start}
          periodEnd={currentRange.end}
          onClose={() => setDrillDown(null)}
        />
      )}
    </div>
  );
};

export default TrainingStatisticsDashboard;
